import { addNotification } from "portal/state/notifications";
import {
  anyContainingPermission,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AtkNav } from "../../AtkNav";
import {
  Button,
  IconButton,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import { capitalize, titleCase } from "common/utils/strings";
import { ConfirmForm } from "./ConfirmForm";
import { CreateJobRequest } from "protos/rtc/jobs";
import { farmApi } from "common/state/portalApi/farm";
import { FarmMapData } from "common/utils/farm";
import { farmToFarmMapData } from "common/hooks/useFarmMapData";
import { Formik, useFormikContext } from "formik";
import {
  formStateToCreateJobRequest,
  initialValues,
  jobFieldIds,
  makeJobName,
  NewJobsFormState,
  stepSequence,
  validRigs,
  WizardStep,
} from "./FormState";
import { getCustomerId } from "common/utils/auth";
import { GroundPrepConfigurePreviewForm } from "./ground-prep/ConfigurePreviewForm";
import { Header } from "portal/components/header/Header";
import { InSeasonConfigurePreviewForm } from "./in-season/ConfigurePreviewForm";
import { JobType } from "protos/atk/job_type";
import { JobTypeForm } from "./JobTypeForm";
import { LaserWeedingConfigurePreviewForm } from "./laser-weeding/ConfigurePreviewForm";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { SearchRigsResponse } from "protos/portal/atk_equipment";
import { typedPath } from "typed-path";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { ZoneSelectorForm } from "./ZoneSelectorForm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useMemo, useState } from "react";

const S = typedPath<NewJobsFormState>();

const stepNames = {
  // this extra level of indirection is a cheap hack to make this compatible
  // with `make lintlocalization`
  [WizardStep.JOB_TYPE]: {
    i18nKey: "models.autotractor.jobType",
  },
  [WizardStep.SELECT_FIELDS]: {
    i18nKey: "views.autotractor.jobCreation.step2SelectFields.title",
  },
  [WizardStep.SELECT_PATH]: {
    i18nKey: "views.autotractor.jobCreation.step2SelectPath.title",
  },
  [WizardStep.CONFIGURE_PATH_AND_PREVIEW]: {
    i18nKey: "views.autotractor.jobCreation.step3ConfigurePreview.title",
  },
  [WizardStep.CONFIRM]: {
    i18nKey: "views.autotractor.jobCreation.step4Confirm.title",
  },
} as const;

export interface NewJobsWizardFormProps {
  customerId: string;
  changeCustomerId: (customerId: string) => void;
  farmMapData: { [farmId: string]: FarmMapData };
  farmsLoading: boolean;
  farmIdByFieldId: { [fieldId: string]: string };
  rigSearchResult: SearchRigsResponse | undefined;
  rigsLoading: boolean;
}

const _WizardForm: React.FC<NewJobsWizardFormProps> = ({
  farmMapData,
  farmsLoading,
  farmIdByFieldId,
  rigSearchResult,
  rigsLoading,
  customerId,
  changeCustomerId,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<NewJobsFormState>();
  let body: JSX.Element | undefined;

  const isInSeason = values.jobType === JobType.IN_SEASON;

  // each job type configures and previews its path differently
  const configurePreviewForm = (): JSX.Element | undefined => {
    switch (values.jobType) {
      case JobType.GROUND_PREP: {
        return (
          <GroundPrepConfigurePreviewForm
            customerId={customerId}
            farmMapData={farmMapData}
            farmIdByFieldId={farmIdByFieldId}
            rigSearchResult={rigSearchResult}
            rigsLoading={rigsLoading}
          />
        );
      }
      case JobType.LASER_WEED: {
        return (
          <LaserWeedingConfigurePreviewForm
            customerId={customerId}
            farmMapData={farmMapData}
            farmIdByFieldId={farmIdByFieldId}
            rigSearchResult={rigSearchResult}
          />
        );
      }
      case JobType.IN_SEASON: {
        return (
          <InSeasonConfigurePreviewForm
            customerId={customerId}
            farmMapData={farmMapData}
            farmIdByFieldId={farmIdByFieldId}
          />
        );
      }
    }
  };

  switch (values.activeStep) {
    case WizardStep.JOB_TYPE: {
      body = <JobTypeForm />;
      break;
    }
    case WizardStep.SELECT_FIELDS: {
      body = (
        <ZoneSelectorForm
          titleI18nKey="views.autotractor.jobCreation.step2SelectFields.title"
          subtitleI18nKey={
            isInSeason
              ? "views.autotractor.jobCreation.step2SelectFields.descriptionOne"
              : "views.autotractor.jobCreation.step2SelectFields.description"
          }
          searchPlaceholderI18nKey="views.autotractor.jobCreation.step2SelectFields.searchFieldNames"
          selectionCountI18nKey="views.autotractor.jobCreation.step2SelectFields.nFieldsSelected"
          customerId={customerId}
          changeCustomerId={changeCustomerId}
          farmMapData={farmMapData}
          farmsLoading={farmsLoading}
          farmIdByFieldId={farmIdByFieldId}
          columns={[
            {
              field: "fieldName",
              headerName: t("views.farms.zoneTypes.field", { count: 1 }),
              flex: 1,
            },
            {
              field: "farmName",
              headerName: capitalize(t("models.farms.farm", { count: 1 })),
              flex: 1,
            },
            {
              field: "plantingHeading",
              headerName: t(
                "views.autotractor.jobCreation.step2SelectFields.plantingHeading"
              ),
              minWidth: 200,
              renderCell: (params) => {
                if (params.row.plantingHeading) {
                  return (
                    <>
                      {params.row.plantingHeading.toLocaleString(undefined, {
                        maximumFractionDigits: 1,
                      })}
                    </>
                  );
                } else {
                  return (
                    <span className="italic">
                      {t("utils.descriptors.unknown")}
                    </span>
                  );
                }
              },
            },
          ]}
          filterZones={(zone) => zone.contents?.field !== undefined}
          // an in-season job works a single field along a single planting path
          checkboxSelection={!isInSeason}
          // don't allow the user to select fields to laser weed for which we don't have
          // planting headings, because we won't know where the rows are
          isZoneSelectable={(zone) => {
            return (
              values.jobType !== JobType.LASER_WEED ||
              zone.contents?.field?.plantingHeading !== undefined
            );
          }}
        />
      );
      break;
    }
    case WizardStep.SELECT_PATH: {
      body = (
        <ZoneSelectorForm
          titleI18nKey="views.autotractor.jobCreation.step2SelectPath.title"
          subtitleI18nKey={
            isInSeason
              ? "views.autotractor.jobCreation.step2SelectPath.inSeasonDescription"
              : "views.autotractor.jobCreation.step2SelectPath.description"
          }
          selectionCountI18nKey="views.autotractor.jobCreation.step2SelectPath.nPathsSelected"
          searchPlaceholderI18nKey="views.autotractor.jobCreation.step2SelectPath.searchPathNames"
          customerId={customerId}
          changeCustomerId={changeCustomerId}
          farmMapData={farmMapData}
          farmsLoading={farmsLoading}
          farmIdByFieldId={farmIdByFieldId}
          columns={[
            {
              field: "fieldName",
              headerName: t("views.farms.zoneTypes.path", { count: 1 }),
              flex: 1,
            },
            {
              field: "farmName",
              headerName: capitalize(t("models.farms.farm", { count: 1 })),
              flex: 1,
            },
            {
              field: "pathOrigin",
              headerName: t(
                "views.autotractor.jobCreation.step2SelectPath.fromLocation"
              ),
              flex: 1,
            },
            {
              field: "pathDestination",
              headerName: t(
                "views.autotractor.jobCreation.step2SelectPath.toLocation"
              ),
              flex: 1,
            },
          ]}
          filterZones={(zone) => zone.contents?.path !== undefined}
          checkboxSelection={false}
        />
      );
      break;
    }
    case WizardStep.CONFIGURE_PATH_AND_PREVIEW: {
      body = configurePreviewForm();
      break;
    }
    case WizardStep.CONFIRM: {
      body = (
        <ConfirmForm
          farmMapData={farmMapData}
          farmIdByFieldId={farmIdByFieldId}
        />
      );
      break;
    }
  }

  const seq = stepSequence(values);
  const activeStepIndex = seq.indexOf(values.activeStep);

  return (
    <>
      <Stepper activeStep={activeStepIndex}>
        {seq.map((step, index) => (
          <Step key={step} index={index}>
            <StepButton
              onClick={() => {
                setFieldValue(S.activeStep.$path, step);
                setFieldTouched(S.activeStep.$path, true);
              }}
            >
              {titleCase(t(stepNames[step].i18nKey))}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {body}
    </>
  );
};

const _NewJobsWizard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const seeAllCustomers = useCanSeeOtherCustomers();

  // customer id; this state is managed outside the form because it needs to be
  // accessible outside the formik context to run queries on behalf of the form
  const { user } = useSelf();
  const selfCustomerId = getCustomerId(user);
  const [customerId, changeCustomerId] = useState(
    selfCustomerId ? selfCustomerId.toString() : ""
  );

  // TODO: most of this wizard should work without farm geometry, except for the
  // path preview, but it would require a refactor of the farmIndexes stuff
  // below
  const { data: farms, isLoading: farmsLoading } = useQueryPopups(
    farmApi.endpoints.listFarms.useQuery({ contents: "full" })
  );

  const [createJobs] = useMutationPopups(
    rtcJobsApi.useCreateBulkJobsMutation({})
  );

  const { data: rigSearchResult, isLoading: rigsLoading } =
    atkEquipmentApi.endpoints.searchRigs.useQuery(
      {
        customerId,
        validate: true,
      },
      { skip: customerId === "" }
    );

  const farmIndexes = useMemo(() => {
    const fmd: {
      farmIdByFieldId: { [fieldId: string]: string };
      farmMapData: { [farmId: string]: FarmMapData };
    } = {
      farmIdByFieldId: {},
      farmMapData: {},
    };

    for (const farm of farms ?? []) {
      if (!farm.id?.id) {
        continue;
      }

      const farmMapData = farmToFarmMapData(farm);

      fmd.farmMapData[farm.id.id] = farmMapData;

      for (const zone of Object.values(farmMapData.zonesById)) {
        if (!zone.id?.id) {
          continue;
        }

        fmd.farmIdByFieldId[zone.id.id] = farm.id.id;
      }
    }

    return fmd;
  }, [farms]);

  const onSubmit = async (values: NewJobsFormState): Promise<void> => {
    let jobsToCreate: CreateJobRequest[] = [];

    const fieldIds = jobFieldIds(
      values,
      farmIndexes.farmMapData,
      farmIndexes.farmIdByFieldId
    );

    for (const fieldId of fieldIds) {
      const farmId = farmIndexes.farmIdByFieldId[fieldId];

      if (!farmId) {
        throw new Error("GroundPrepConfirmForm: no farm ID for field?");
      }

      const farm = farmIndexes.farmMapData[farmId];

      if (!farm) {
        throw new Error("GroundPrepConfirmForm: couldn't get farm data");
      }

      switch (values.jobType) {
        case JobType.GROUND_PREP: {
          jobsToCreate = formStateToCreateJobRequest(
            farm,
            (fieldName) => makeJobName(values, fieldName),
            customerId,
            fieldId,
            false,
            values,
            {
              rigIds: rigSearchResult
                ? validRigs(rigSearchResult, values).map((rig) => rig.id)
                : [],
            }
          );
          break;
        }
        case JobType.LASER_WEED:
        case JobType.TRANSPORT:
        case JobType.IN_SEASON: {
          jobsToCreate = formStateToCreateJobRequest(
            farm,
            (fieldName) => makeJobName(values, fieldName),
            customerId,
            fieldId,
            false,
            values,
            {}
          );
          break;
        }
      }
    }

    if (jobsToCreate.length === 0) {
      dispatch(
        addNotification({
          message: t("views.autotractor.jobCreation.noValidRigs"),
          variant: "error",
        })
      );
      return;
    }

    const result = await createJobs({ jobRequests: jobsToCreate });

    if (!result.error) {
      navigate(Path.JOBS);
    }
  };

  return (
    <Page>
      <Header title={t("models.autotractor.autonomy")}>
        <AtkNav />
      </Header>
      <div className="flex flex-col gap-5">
        <div className="flex gap-2">
          <IconButton color="info" onClick={() => navigate(Path.JOBS)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" className="my-auto">
            {t("views.autotractor.jobCreation.title")}
          </Typography>
          <span className="flex-1" />
          <Button
            color="info"
            variant="text"
            size="large"
            onClick={() => navigate(Path.JOBS)}
          >
            {t("utils.actions.cancel")}
          </Button>
        </div>
        <Formik
          initialValues={initialValues(seeAllCustomers)}
          onSubmit={onSubmit}
        >
          <_WizardForm
            customerId={customerId}
            changeCustomerId={changeCustomerId}
            farmsLoading={farmsLoading}
            farmMapData={farmIndexes.farmMapData}
            farmIdByFieldId={farmIndexes.farmIdByFieldId}
            rigSearchResult={rigSearchResult}
            rigsLoading={rigsLoading}
          />
        </Formik>
      </div>
    </Page>
  );
};

export const NewJobsWizard = withAuthorizationRequired(
  anyContainingPermission(
    PermissionAction.update,
    PermissionResource.autotractor_jobs,
    PermissionDomain.customer
  ),
  _NewJobsWizard
);
